import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { showProQuestSearch } from "config";
import DeleteConfirmationDialog from "features/Settings/components/DeleteConfirmationDialog";

import { useSearchCredentials } from "../hooks";

const MASKED_VALUE = "••••••••";

const allProviders = [
  {
    credentialType: "ebsco",
    title: "EBSCO",
    introduction: "If you are an existing EBSCO customer and know your EBSCO API credentials for Sooth.fyi, enter them below.",
    usernameLabel: "EBSCO API User ID",
    passwordLabel: "EBSCO API Password",
    profileLabel: "EBSCO Profile ID",
    description:
      "EBSCO is the leading provider of research databases, e-journals, magazine subscriptions, ebooks and discovery service for academic libraries, public libraries, corporations, schools, government and medical institutions.",
  },
  {
    credentialType: "proQuest",
    title: "ProQuest",
    introduction: "If you are an existing ProQuest customer and know your ProQuest Federated Search credentials for Sooth.fyi, enter them below.",
    usernameLabel: "ProQuest Federated Search Username",
    passwordLabel: "ProQuest Federated Search Password",
    description:
      "ProQuest is a leading global information and technology company that provides research databases, tools, and digital content to academic, public, school, and corporate libraries.",
  },
];

const providers = allProviders.filter(
  (provider) => provider.credentialType !== "proQuest" || showProQuestSearch
);

function maskValue(value) {
  return value ? MASKED_VALUE : "Not provided";
}

function ProviderCredentialSection({
  provider,
  credential,
  isLoading,
  isSaving,
  isDeleting,
  onSave,
  onDelete,
}) {
  const [username, setUsername] = useState(credential.username);
  const [profile, setProfile] = useState(credential.profile);
  const [password, setPassword] = useState("");
  const [isEditing, setIsEditing] = useState(!credential.hasSavedCredential);
  const [error, setError] = useState(null);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setUsername(credential.username);
    setProfile(credential.profile);
    if (!isLoading) setIsEditing(!credential.hasSavedCredential);
  }, [credential.username, credential.profile, credential.hasSavedCredential, isLoading]);

  const fieldsLocked = credential.hasSavedCredential && !isEditing;
  const canSave =
    !fieldsLocked &&
    !isLoading &&
    username.trim() &&
    !isSaving &&
    (credential.hasSavedCredential || password.trim());

  async function handleSave() {
    setError(null);
    const response = await onSave({
      credentialType: provider.credentialType,
      username: username.trim(),
      password: password.trim(),
      profile: provider.profileLabel ? profile.trim() || null : null,
      isEnabled: true,
    });

    if (response.success) {
      setPassword("");
      setIsEditing(false);
      toast.success(`${provider.title} credentials saved and verified.`);
    } else {
      setError(response.error);
    }
  }

  async function handleToggle(_event, enabled) {
    setError(null);
    setIsToggling(true);
    try {
    const response = await onSave({
      credentialType: provider.credentialType,
      username: credential.username,
      password: "",
      profile: provider.profileLabel ? credential.profile || null : null,
      isEnabled: enabled,
    });

    if (response.success) {
      toast.success(
        `${provider.title} is now ${enabled ? "enabled" : "disabled"}.`
      );
    } else {
      setError(response.error);
    }
    } finally {
      setIsToggling(false);
    }
  }

  async function handleDelete() {
    setError(null);
    const response = await onDelete(provider.credentialType);
    setIsDeleteDialogOpen(false);

    if (response.success) {
      setUsername("");
      setProfile("");
      setPassword("");
      setIsEditing(true);
      toast.success(`${provider.title} credentials deleted.`);
    } else {
      toast.error(response.error);
    }
  }

  return (
    <Stack spacing={1.5} py={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography component="h2" variant="h4">
          {provider.title}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>Off</Typography>
          {isLoading ? (
            <Skeleton variant="rounded" width={58} height={26} aria-label={`Loading ${provider.title} status`} />
          ) : (
          <Tooltip
            title={
              credential.hasSavedCredential
                ? "Enable or disable this search provider for the group."
                : `Save valid ${provider.title} credentials before enabling this provider.`
            }
          >
            <span>
              <Switch
                checked={credential.isEnabled}
                onChange={handleToggle}
                disabled={!credential.hasSavedCredential || isSaving || isToggling}
              />
            </span>
          </Tooltip>
          )}
          <Typography>On</Typography>
          <Box sx={{ width: 16, height: 16, display: "flex" }}>
            {isToggling && (
              <CircularProgress
                size={16}
                aria-label={`Updating ${provider.title} status`}
              />
            )}
          </Box>
        </Stack>
      </Stack>

      <Typography>{provider.description}</Typography>
      <Typography>{provider.introduction}</Typography>

      {isLoading ? (
        <Stack spacing={1.5} maxWidth="sm" aria-busy="true" aria-label={`Loading ${provider.title} credentials`}>
          {[provider.usernameLabel, provider.passwordLabel, provider.profileLabel]
            .filter(Boolean)
            .map((label) => (
              <Box key={label}>
                <Typography>{label}</Typography>
                <Skeleton variant="rounded" height={40} />
              </Box>
            ))}
        </Stack>
      ) : fieldsLocked ? (
        <Stack spacing={0.5} maxWidth="sm">
          <Typography>
            <strong>{provider.usernameLabel}:</strong> {maskValue(credential.username)}
          </Typography>
          <Typography>
            <strong>{provider.passwordLabel}:</strong> {MASKED_VALUE}
          </Typography>
          {provider.profileLabel && (
            <Typography>
              <strong>{provider.profileLabel}:</strong> {maskValue(credential.profile)}
            </Typography>
          )}
          <Box>
            <Tooltip title={`Edit ${provider.title} credentials`}>
              <IconButton
                aria-label={`Edit ${provider.title} credentials`}
                onClick={() => setIsEditing(true)}
                disabled={isDeleting}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={`Delete ${provider.title} credentials`}>
              <IconButton
                aria-label={`Delete ${provider.title} credentials`}
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={isDeleting || isToggling}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <DeleteConfirmationDialog
            open={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onConfirm={handleDelete}
            loading={isDeleting}
            title={`Delete ${provider.title} credentials?`}
            message={`${provider.title} will be disabled and removed for everyone in your Group. You can add credentials again at any time.`}
          />
        </Stack>
      ) : (
        <Stack spacing={1.5} maxWidth="sm">
          <TextField
            label={provider.usernameLabel}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={isSaving}
            required
            fullWidth
          />
          <TextField
            label={provider.passwordLabel}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={credential.hasSavedCredential ? MASKED_VALUE : ""}
            helperText={
              credential.hasSavedCredential
                ? "Leave blank to keep the current password."
                : ""
            }
            disabled={isSaving}
            required={!credential.hasSavedCredential}
            fullWidth
          />
          {provider.profileLabel && <TextField
            label={provider.profileLabel}
            value={profile}
            onChange={(event) => setProfile(event.target.value)}
            disabled={isSaving}
            fullWidth
          />}
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Button
              type="button"
              variant="contained"
              onClick={handleSave}
              disabled={!canSave}
              sx={{ display: "flex", gap: 1 }}
            >
              {isSaving && <CircularProgress size={20} />}
              Save & Verify
            </Button>
          </Box>
        </Stack>
      )}

      {provider.credentialType === "ebsco" ? (
        <Stack spacing={1.5}>
          <Typography>
            If you are an existing EBSCO customer but don't know your institution's
            API credentials for Sooth.fyi, contact EBSCO support and request an{" "}
            <Box component="span" sx={{ textDecoration: "underline" }}>API profile for use with Sooth.fyi</Box>.
            You can contact EBSCO support using{" "}
            <Link underline="always" href="https://connect.ebsco.com/s/contactsupport" target="_blank" rel="noopener noreferrer">this link</Link>{" "}
            (note: EBSCO login credentials are required to connect with their support).
            Be sure to include your school / district name and address so they can identify your account quickly.
          </Typography>
          <Box>
            <Typography>
              <strong>Troubleshooting Tip:</strong> To ensure that all your EBSCO content appears in your Sooth results, make sure to do the following:
            </Typography>
            <Box component="ol" sx={{ my: 1, pl: 3 }}>
              <Typography component="li">
                Log in to <Link href="https://eadmin.ebscohost.com/" underline="always" target="_blank" rel="noopener noreferrer">EBSCOadmin</Link>
              </Typography>
              <Typography component="li">
                From the Choose Profile dropdown, select <strong>API profile for Sooth.fyi (sooth)</strong>
              </Typography>
              <Typography component="li">
                Select the <strong>Databases</strong> tab and customize the databases as needed
              </Typography>
            </Box>
            <Typography>
              You can also contact <Link href="https://connect.ebsco.com/s/contactsupport" underline="always" target="_blank" rel="noopener noreferrer">EBSCO Support</Link> for assistance.
            </Typography>
          </Box>
          <Typography>
            If you get your EBSCO access indirectly through your state or a similar
            type of program, you may also be eligible for an API profile for Sooth.fyi.
            If this applies to you, please follow the instructions above and also include
            which state or program through which you currently receive EBSCO access.
          </Typography>
          <Typography>
            To learn more about how this integration works, you can read EBSCO's documentation{" "}
            <Link href="https://connect.ebsco.com/s/article/Sooth-fyi-Integration-with-EBSCO" underline="always" target="_blank" rel="noopener noreferrer">here</Link>.
          </Typography>
          <Typography>
            Please note that your Group on Sooth can only use{" "}
            <Box component="span" sx={{ textDecoration: "underline" }}>one set</Box>{" "}
            of EBSCO credentials.
            Everyone in your Group will have access to this EBSCO content. Please make sure
            that your EBSCO subscription is intended to support your whole Group on Sooth.
            If your Group includes multiple schools who each have different EBSCO subscriptions,
            please contact us to discuss separating your Group into sub-groups to accommodate
            different EBSCO subscriptions.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Typography>
            If you are an existing ProQuest customer but don't know your institution's
            Federated Search credentials for Sooth.fyi, contact their support via email at{" "}
            <Link underline="always" href="mailto:email.technicalsupport@proquest.com">email.technicalsupport@proquest.com</Link>{" "}
            and request your{" "}
            <Box component="span" sx={{ textDecoration: "underline" }}>Federated Search credentials for Sooth.fyi</Box>.
            In your email, you must include your school name and address so they can identify your account quickly.
          </Typography>
          <Typography>
            If you get your ProQuest access indirectly through your state or a similar
            type of program, you may also be eligible for Federated Search credentials
            for Sooth.fyi. If this applies to you, please follow the instructions above
            and also include which state or program through which you currently receive ProQuest access.
          </Typography>
          <Typography>
            Please note that your Group on Sooth can only use{" "}
            <Box component="span" sx={{ textDecoration: "underline" }}>one set</Box>{" "}
            of ProQuest credentials.
            Everyone in your Group will have access to this ProQuest content. Please make sure
            that your ProQuest subscription is intended to support your whole Group on Sooth.
            If your Group includes multiple schools who each have different ProQuest subscriptions,
            please contact us to discuss separating your Group into sub-groups to accommodate
            different ProQuest subscriptions.
          </Typography>
        </Stack>
      )}
        <Typography>
          If you are not an existing {provider.title} customer and would like to explore
          a subscription (or upgrade an existing one), {provider.credentialType === "ebsco" ? (
            <>
              you can contact their sales team{" "}
              <Link href="https://about.ebsco.com/contact" underline="always" target="_blank" rel="noopener noreferrer">here</Link>.
            </>
          ) : (
            <>
              visit <Link href="https://about.proquest.com/en/" underline="always" target="_blank" rel="noopener noreferrer">https://about.proquest.com/en/</Link> and click on &quot;Contact Us&quot;.
            </>
          )}
        </Typography>
    </Stack>
  );
}

export default function DatabaseIntegration() {
  const {
    credentials,
    isLoading,
    savingType,
    deletingType,
    saveCredential,
    deleteCredential,
  } = useSearchCredentials();

  return (
    <Stack pl={3}>
      {providers.map((provider, index) => (
        <div key={provider.credentialType}>
          {index > 0 && <Divider />}
          <ProviderCredentialSection
            provider={provider}
            credential={credentials[provider.credentialType]}
            isLoading={isLoading}
            isSaving={savingType === provider.credentialType}
            isDeleting={deletingType === provider.credentialType}
            onSave={saveCredential}
            onDelete={deleteCredential}
          />
        </div>
      ))}
    </Stack>
  );
}
